import { createFileRoute } from '@tanstack/react-router';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { TextInput } from '@interxfi/design-system/components/input/text-input/text-input';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import SearchIcon from '@interxfi/design-system/icons/react/icon-search';
import { useSearch } from '@interxfi/utilities/search/debounced-search';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { JpmamEmulatorPositionsTable } from '../../../../../../../../../features/vendor/jpmam/emulator/position/jpmam-emulator-positions-table';
import { JpmamEmulatorSetPositionDrawer } from '../../../../../../../../../features/vendor/jpmam/emulator/position/jpmam-emulator-set-position-drawer';
import { JpmamVendorTabRail } from '../../../../../../../../../features/vendor/jpmam/jpmam-vendor-tab-rail';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/jpm/emulator/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingVendorJpmam,
      label: 'Emulator',
      order: 5,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingVendorJpmam>,
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'jpmam-emulator-position-list',
        label: 'J.P. Morgan',
        to: '/clearing/vendor/jpm/emulator',
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('JPMAM Emulator'),
  component: JpmamEmulatorPositionList,
});

// Hardcoded value from Chris G. that's used in simulated environments.
const SIMULATED_ENVIRONMENT_JPMAM_ACCOUNT_ID = '33311';

function JpmamEmulatorPositionList() {
  const { searchValue, realTimeSearchValue: realTimeAccountId, onSearchChange } = useSearch(SIMULATED_ENVIRONMENT_JPMAM_ACCOUNT_ID);

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <VerticalStack gap={7}>
          <JpmamVendorTabRail
            actions={
              <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                <RequiresAccess write>
                  <JpmamEmulatorSetPositionDrawer
                    trigger={<Button affixIcon={<EditIcon aria-hidden />} aria-label="Set JPMAM Account Position" size="small" />}
                  />
                </RequiresAccess>
              </HorizontalStack>
            }
          />

          <VerticalStack gap={4}>
            <TextInput
              hideLabel
              label="Search"
              name="jpmam-emulator-position-account-search"
              onChange={onSearchChange}
              placeholder="Account ID"
              prefix={<SearchIcon aria-hidden width={16} />}
              role="search"
              size="small"
              value={realTimeAccountId}
            />

            <JpmamEmulatorPositionsTable accountId={searchValue} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
